import { apiFetch } from "../../apiClient";

/**
 * The user's saved setup as data-feed renders it for Chat: a `<saved_context>` block and its
 * system rule.
 */
export interface SavedContext {
  block: string;
  rule: string;
}

const SAVED_CONTEXT_PATH = "/api/finster/onboarding/saved-context";
const SAVED_CONTEXT_TIMEOUT_MS = 3_000;

/**
 * Reads the latest saved setup for this run, like a new Chat session. Never throws: a failed or
 * slow read means no setup, so a run never waits on it or fails because of it. The outcome goes
 * to the host's run log, never the setup itself.
 */
export async function fetchSavedContext(
  log?: (message: string) => void
): Promise<SavedContext | null> {
  const startedAt = Date.now();
  const report = (status: string, outcome: string) =>
    log?.(
      `👤 Saved setup: GET ${SAVED_CONTEXT_PATH} → ${status} in ${Date.now() - startedAt}ms, ${outcome}`
    );
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), SAVED_CONTEXT_TIMEOUT_MS);
  try {
    const response = await apiFetch(SAVED_CONTEXT_PATH, {
      method: "GET",
      signal: controller.signal,
    });
    if (!response.ok) {
      report(String(response.status), "running without it");
      return null;
    }
    const { data } = (await response.json()) as {
      data?: { block?: unknown; rule?: unknown } | null;
    };
    if (typeof data?.block !== "string" || typeof data.rule !== "string") {
      report(String(response.status), "none for this user");
      return null;
    }
    report(String(response.status), `loaded ${data.block.length} characters`);
    return { block: data.block, rule: data.rule };
  } catch (error) {
    report(controller.signal.aborted ? "no answer" : String(error), "running without it");
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/** The saved setup goes first, ahead of Skill guidance, as in Chat. */
export function prependSavedContext(
  preludes: readonly string[] | undefined,
  savedContext: SavedContext | null | undefined
): readonly string[] | undefined {
  return savedContext ? [savedContext.block, ...(preludes ?? [])] : preludes;
}

export function appendSavedContextRule(
  instructions: string,
  savedContext: SavedContext | null | undefined
): string {
  return savedContext ? `${instructions}\n\n${savedContext.rule}` : instructions;
}
